import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { api } from "../services/api";
import { useNavigate } from "react-router-dom";
import UserRegistrationPairKey from "../feature/utils/userRegistrationPairKey";
import {
  importarDadosIndexedDB,
  limparTodosDadosIndexedDB,
  loadPrivateKeyFromIndexedDB,
  savePrivateKeyToIndexedDB,
} from "../feature/utils/indexedDB";
import { useAuthStore, UserAuth } from "../store/useAuthStore";
import { getInformationsFromDevice } from "../mobile/ConnectedDevices";
import { isMobileCheck } from "../utils/checkIsMobile";
import { load as LoadFingerPrint } from "@fingerprintjs/fingerprintjs";
import { getDeviceId } from "../utils/getFingerPrintDevice";
import { useSocketStore } from "../services/socket-io";
import {
  restoreMessagesFromMirrorIfNeeded,
  syncDesktopMirrorNow,
} from "../feature/utils/mirrorSync";
import { useChatStore } from "../store/chatStore";

const PUBLIC_AUTH_PATHS = ["/", "/cadastro", "/signin/backoffice", "/logout"];

export interface User {
  id: string;
  status: string;
  name: string;
  userName: string;
  hardPassword: string;
  password_hash: string;
  created_at: string;
  role: string;
  type: string;
  privateKey: string;
  publicKey: string;
  groups: {
    id: string; // ID do grupo
    name: string; // Nome do grupo
    description: string; // Descrição do grupo
    status: string; // Status do grupo (ex: "active")
    owner: string; // Nome de usuário do dono
    ownerId: string; // ID do dono
    memberCount: number; // Quantidade de membros no grupo
    role: string; // Função do usuário dentro do grupo (ex: "owner")
    type: string; // Tipo de grupo (ex: "GROUP")
  };
}

type SignInCredentials = {
  userName: string;
  password_hash: string;
  deviceId: string;
};

type SignInQRCode = {
  accessToken: string;
  user: User;
};

interface AuthContextProps {
  user: UserAuth | undefined;
  onSignIn(credentials: SignInCredentials): Promise<{ token: string; user: UserAuth }>;
  onSignInQRCode(credentials: SignInQRCode): Promise<void>;
  onLogout: (isMobile: boolean) => void;
  showKeyModal: boolean; // Controle do modal
  setShowKeyModal: React.Dispatch<React.SetStateAction<boolean>>; // Função para atualizar o modal
  isAuthenticated: boolean; // Adicionado para corresponder ao valor do provider
  onExpireToken: () => void;
}

interface AuthProviderProps {
  children: React.ReactNode;
}

const AuthContext = createContext({} as AuthContextProps);

const AuthProvider = ({ children }: AuthProviderProps) => {
  const [isAuthenticated, setIsAuthenticate] = useState(false);
  const [showKeyModal, setShowKeyModal] = useState(false); // Novo estado para o modal
  const { socket, disconnectSocket, connectSocket, sendUserConnect } = useSocketStore();
  const logoutInFlightRef = useRef(false);

  const navigate = useNavigate();

  const authStore = useAuthStore();
  const currentUser = authStore.getUser(); // Pega o usuário do store
  const chatStore = useChatStore();

  const clearLocalSession = useCallback(() => {
    localStorage.removeItem("@backoffice:token");
    localStorage.removeItem("@backoffice:user");
    localStorage.removeItem("auth-storage");
    // Preserve lazy-retry:* keys so chunk recovery cannot loop after logout.
    const lazyRetryKeys: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key?.startsWith("lazy-retry:")) lazyRetryKeys.push(key);
    }
    const preserved = lazyRetryKeys.map((key) => [key, sessionStorage.getItem(key)] as const);
    sessionStorage.clear();
    for (const [key, value] of preserved) {
      if (value != null) sessionStorage.setItem(key, value);
    }
    authStore.clearUser();
    disconnectSocket();
    setIsAuthenticate(false);
  }, [authStore, disconnectSocket]);

  const runMirrorRestoreAndRefresh = useCallback(
    async (user?: UserAuth, token?: string) => {
      if (!user?.id || !token) return;

      try {
        const restoreResult = await restoreMessagesFromMirrorIfNeeded({
          userId: user.id,
        });

        if (restoreResult.restored && user.userName) {
          console.log("[MIRROR] Restore concluído no cliente:", restoreResult);
          await chatStore.fetchChats(user.id, user.userName, token);
        }
      } catch (error) {
        console.warn("[MIRROR] Restore não concluído:", error);
      }
    },
    [chatStore],
  );

  useEffect(() => {
    const handleConnect = () => {
      console.log("[AUTH_PROVIDER_SOCKET_EVENT] Socket conectado!");
      // Se o backend não gerencia 'user_connected' na conexão do servidor automaticamente:
      if (currentUser?.id) {
        const isMobile = isMobileCheck();

        socket?.emit("user_connected", { userId: currentUser.id, isMobile });
        console.log("[AUTH_PROVIDER_SOCKET_EVENT] Emitindo user_connected para:", currentUser.id);
      }
    };
    const handleDisconnect = () => {
      console.log("[AUTH_PROVIDER_SOCKET_EVENT] Socket desconectado.");
    };
    const handleConnectError = (error: Error) => {
      console.error("[AUTH_PROVIDER_SOCKET_EVENT] Erro de conexão do socket:", error);
    };

    socket?.on("connect", handleConnect);
    socket?.on("disconnect", handleDisconnect);
    socket?.on("connect_error", handleConnectError);

    // Atualiza o status inicial caso o socket já esteja conectado ao montar
    // (ex: se o AuthProvider for remontado mas o socket persis

    return () => {
      socket?.off("connect", handleConnect);
      socket?.off("disconnect", handleDisconnect);
      socket?.off("connect_error", handleConnectError);
    };
  }, [currentUser?.id]);

  const onExpireToken = () => {
    console.warn("[AUTH_DEBUG] 🐞 Forçando a remoção do token do localStorage...");
    localStorage.removeItem("@backoffice:token");

    // Para tornar o teste imediato, vamos recarregar a página.
    // A lógica de inicialização no AuthProvider tentará encontrar o token,
    // não encontrará, e o interceptor da API fará o resto na próxima chamada.
    alert(
      "Token removido do localStorage. A página será recarregada para simular o que acontece quando o app é reaberto com um token inválido."
    );
    window.location.reload();
  };

  const isAuthFailure = (error: any): boolean => {
    const status = error?.response?.status;
    return status === 401 || status === 403;
  };
  const isDesktopClient = () => !isMobileCheck();

  const revalidateSessionSafely = useCallback(async () => {
    const token = localStorage.getItem("@backoffice:token");
    if (!token) return false;

    const validateOnce = async () => {
      const response = await api.get("/me", {
        timeout: 10000,
        ...( { skipUnauthorizedHandler: true } as any),
      });
      if (response.data?.user) {
        refreshTokenFromResponse(response.data);
      }
      return Boolean(response.data?.user);
    };

    try {
      if (await validateOnce()) return true;
    } catch (error) {
      console.warn("[AUTH] Primeira tentativa de revalidação falhou:", error);
    }

    await new Promise((resolve) => setTimeout(resolve, 1500));

    try {
      return await validateOnce();
    } catch (error) {
      console.warn("[AUTH] Segunda tentativa de revalidação falhou:", error);
      return false;
    }
  }, []);

  const onSignInQRCode = async ({ accessToken, user }: SignInQRCode) => {
    localStorage.setItem("@backoffice:token", accessToken);

    setIsAuthenticate(true);
    authStore.setUser(user);

    const privateKey = await loadPrivateKeyFromIndexedDB(user.id);

    if (!privateKey) {
      setShowKeyModal(true);
      console.log("Nenhuma chave privada encontrada. Gerando nova chave...");
    } else {
      console.log("Chave privada carregada do IndexedDB.");
    }

    connectSocket();

    const isMobile = isMobileCheck();
    socket?.emit("user_connected", { userId: user.id, isMobile });

    if (!isMobile) {
      void syncDesktopMirrorNow({
        userId: user.id,
        trigger: "signin-qrcode",
        force: true,
      }).catch((error) => {
        console.warn("[MIRROR] Falha no sync desktop pós QR Code:", error);
      });
    } else {
      void runMirrorRestoreAndRefresh(user as UserAuth, accessToken);
    }

    navigate("/backoffice");
  };

  const onSignIn = async ({ userName, password_hash, deviceId }: SignInCredentials) => {
    window.localStorage.removeItem("@stricv2:status");
    window.localStorage.removeItem("@stricv2:id");
    const isMobile = isMobileCheck();
    const isTouchMac = navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
    const isMobileUserAgent =
      /Android|iPhone|iPad|iPod|Windows Phone|webOS|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || isTouchMac;
    const isStandalonePWA =
      window.matchMedia("(display-mode: standalone)").matches ||
      (window.navigator as any).standalone === true ||
      document.referrer.startsWith("android-app://");
    const shouldBlockMobileBrowserLogin = (isMobile || isMobileUserAgent) && !isStandalonePWA;

    // Regra de segurança: impedir login em navegador mobile (permitido apenas PWA instalada).
    if (shouldBlockMobileBrowserLogin) {
      throw new Error("Este acesso nao permite login por navegador. Instale o app para comecar a usar.");
    }
    let deviceIdEstable = "";
    try {
      deviceIdEstable = await getDeviceId(isMobile ? "mobile" : "desktop");
    } catch (deviceError) {
      console.error("[AUTH] Falha ao obter identificador do dispositivo:", deviceError);
      throw new Error("Nao foi possivel preparar o login neste navegador. Instale o app e tente novamente.");
    }
    const { browserName, deviceTypeName } = getInformationsFromDevice();
    console.log();
    const doLogin = () =>
      api.post("/sessions", {
        userName,
        password_hash,
        isMobile,
        isStandalonePWA,
        deviceId: deviceIdEstable,
        browserName,
        deviceTypeName,
      });

    try {
      let response: Awaited<ReturnType<typeof doLogin>> | undefined;

      try {
        response = await doLogin();
      } catch (firstErr: any) {
        const isTransient =
          !firstErr.response ||
          firstErr.code === "ECONNABORTED" ||
          firstErr.response?.status >= 500;

        if (isTransient) {
          console.warn("[AUTH] Primeira tentativa de login falhou, retentando em 2s...", firstErr);
          await new Promise((r) => setTimeout(r, 2000));
          response = await doLogin();
        } else {
          throw firstErr;
        }
      }

      if (!response || !response.data) throw new Error("Servidor nao retornou dados. Tente novamente.");

      const { token, user } = response.data;
      localStorage.setItem("@backoffice:token", token);
      api.defaults.headers.common.Authorization = `Bearer ${token}`;

      if (user) {
        authStore.setUser(user);
      }

      setIsAuthenticate(true);

      navigate("/backoffice");

      Promise.resolve().then(async () => {
        try {
          if (response!.data.user.dataSync) {
            const syncWithTimeout = Promise.race([
              importarDadosIndexedDB("ChatAppDB", response!.data.user.dataSync),
              new Promise((resolve) => setTimeout(() => resolve("timeout"), 4500)),
            ]);
            const responseSync = await syncWithTimeout;
            console.log("[AUTH] IndexedDB sync result:", responseSync);
          }
        } catch (syncError) {
          console.error("[AUTH] Erro no sync do IndexedDB (background):", syncError);
        }

        try {
          const privateKey = await loadPrivateKeyFromIndexedDB(user.id);
          if (!privateKey) setShowKeyModal(true);
        } catch (keyError) {
          console.error("[AUTH] Erro ao carregar chave privada:", keyError);
        }

        try {
          sendUserConnect({ userId: user.id, isMobile });
        } catch (socketError) {
          console.error("[AUTH] Erro ao sincronizar socket no login:", socketError);
        }

        if (!isMobile) {
          void syncDesktopMirrorNow({
            userId: user.id,
            trigger: "signin-password",
            force: true,
          }).catch((error) => {
            console.warn("[MIRROR] Falha no sync desktop pós login:", error);
          });
        } else {
          await runMirrorRestoreAndRefresh(user as UserAuth, token);
        }
      });

      return response.data;
    } catch (err: any) {
      localStorage.removeItem("@backoffice:token");

      const backendCode = err?.response?.data?.code;
      const backendMessage = err?.response?.data?.message || "";
      const httpStatus = err?.response?.status;

      let errorMessage: string;

      if (backendCode === "USER_PENDING_ACTIVATION") {
        errorMessage =
          "Cadastro pendente de aprovação. Aguarde ativação em até 24h para fazer login.";
      } else if (String(backendMessage).toLowerCase().includes("dispositivo")) {
        errorMessage =
          "Este dispositivo não está autorizado para esta conta. Se seu cadastro é novo, aguarde ativação no backoffice.";
      } else if (String(backendMessage).toLowerCase().includes("não permitido") || String(backendMessage).toLowerCase().includes("nao permitido")) {
        errorMessage = backendMessage;
      } else if (err.code === "ECONNABORTED" || err.message?.includes("timeout")) {
        errorMessage = "O servidor demorou muito para responder. Verifique sua conexao e tente novamente.";
      } else if (!err.response) {
        errorMessage = "Sem conexao com o servidor. Verifique sua internet e tente novamente.";
      } else if (httpStatus >= 500) {
        errorMessage = "O servidor esta temporariamente indisponivel. Tente novamente em alguns segundos.";
      } else {
        const fullError = `${backendMessage} ${String(err?.message || "")}`.toLowerCase();
        if (fullError.includes("indexeddb") || fullError.includes("can't find variable")) {
          errorMessage = "Este acesso nao permite login por navegador. Instale o app para comecar a usar.";
        } else {
          errorMessage = backendMessage || "Nao foi possivel fazer login. Tente novamente.";
        }
      }

      throw new Error(errorMessage);
    }
  };
  const onLogout = useCallback(
    async (isMobile: boolean | null) => {
      // Avoid token→401→replace("/")→token still briefly present loops.
      if (logoutInFlightRef.current) {
        console.log("[AUTH] Logout já em andamento — ignorando chamada duplicada");
        return;
      }
      logoutInFlightRef.current = true;
      console.log("[AUTH] ⚠️ Iniciando logout...");

      const userId = currentUser?.id;
      const path = window.location.pathname || "/";
      const alreadyOnPublicAuthPage = PUBLIC_AUTH_PATHS.includes(path);

      // Clear credentials FIRST (sync) so the next boot never re-enters /me→401→logout.
      clearLocalSession();

      // Backend logout is best-effort and must not block / re-trigger unauthorized loops.
      void (async () => {
        try {
          const deviceIdEstable = await getDeviceId(isMobile ? "mobile" : "desktop");
          await api.post(
            "/auth/logout",
            {
              idDevice: isMobile ? deviceIdEstable : null,
              userId,
            },
            { ...( { skipUnauthorizedHandler: true } as any) }
          );
        } catch (error) {
          console.error("[AUTH] Erro ao fazer logout no backend:", error);
        }
      })();

      if (alreadyOnPublicAuthPage) {
        console.log("[AUTH] Já na tela de login — sem hard reload");
        logoutInFlightRef.current = false;
        return;
      }

      console.log("[AUTH] Redirecionando para login (soft navigate)");
      try {
        navigate("/", { replace: true });
      } catch {
        window.location.replace("/");
      } finally {
        // Allow a future explicit logout after navigation settles.
        setTimeout(() => {
          logoutInFlightRef.current = false;
        }, 1500);
      }
    },
    [currentUser, clearLocalSession, navigate]
  );

  useEffect(() => {
    const handleUnauthorized = () => {
      console.log("[AUTH] Evento 'unauthorized' recebido. Executando logout...");
      const isMobile = isMobileCheck(); // Você já tem uma função para isso
      (async () => {
        // Desktop: tenta recuperar sessão antes de deslogar para evitar quedas espúrias.
        if (!isMobile) {
          const sessionRecovered = await revalidateSessionSafely();
          if (sessionRecovered) {
            console.log("[AUTH] Sessão desktop recuperada após 401 transitório.");
            return;
          }
        }
        onLogout(isMobile);
      })();
    };

    // Adiciona o listener
    window.addEventListener("unauthorized", handleUnauthorized);

    // Função de limpeza para remover o listener quando o componente for desmontado
    return () => {
      window.removeEventListener("unauthorized", handleUnauthorized);
    };
  }, [onLogout, revalidateSessionSafely]);

  const refreshTokenFromResponse = (responseData: any) => {
    const newToken = responseData?.user?.access_token;
    if (newToken && typeof newToken === "string") {
      localStorage.setItem("@backoffice:token", newToken);
      api.defaults.headers.common.Authorization = `Bearer ${newToken}`;
      console.log("[AUTH] Token renovado silenciosamente via /me");
    }
  };

  const initialize = async () => {
    console.log("[AUTH] Inicializando autenticação...");
    const token = localStorage.getItem("@backoffice:token");

    if (!token) {
      console.log("[AUTH] Nenhum token encontrado");
      setIsAuthenticate(false);
      authStore.clearUser();
      disconnectSocket();
      return;
    }

    api.defaults.headers.common.Authorization = `Bearer ${token}`;

    try {
      console.log("[AUTH] Verificando token com /me...");
      // skipUnauthorizedHandler: initialize already handles 401; avoid double logout + reload loop.
      const response = await api.get("/me", {
        ...( { skipUnauthorizedHandler: true } as any),
      });
      const user = response.data?.user;

      if (user) {
        console.log("[AUTH] Usuário autenticado:", user.userName);
        refreshTokenFromResponse(response.data);
        authStore.setUser(user);
        setIsAuthenticate(true);
        connectSocket();
        if (isDesktopClient()) {
          void syncDesktopMirrorNow({
            userId: user.id,
            trigger: "initialize",
          }).catch((error) => {
            console.warn("[MIRROR] Falha no sync desktop na inicialização:", error);
          });
        } else {
          void runMirrorRestoreAndRefresh(user, token);
        }
      } else {
        console.log("[AUTH] Resposta inválida do /me — limpando sessão sem hard reload");
        clearLocalSession();
      }
    } catch (error) {
      console.error("[AUTH] Erro ao verificar autenticação:", error);
      if (isAuthFailure(error)) {
        // Soft clear only: hard replace("/") here caused infinite flicker when /me kept returning 401.
        console.log("[AUTH] Token inválido na inicialização — sessão limpa, ficando na tela de login");
        clearLocalSession();
      } else {
        const cachedUser = authStore.getUser();
        if (cachedUser) {
          console.log("[AUTH] Erro transitório no /me. Mantendo sessão local.");
          setIsAuthenticate(true);
          connectSocket();
        }
      }
    }
  };

  useEffect(() => {
    initialize();
  }, []);

  // Verificar autenticação quando o app volta do segundo plano
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === "visible") {
        console.log("[AUTH] 👀 App voltou ao primeiro plano, verificando token...");
        const token = localStorage.getItem("@backoffice:token");

        // Se não tem token mas está marcado como autenticado = problema
        if (!token && isAuthenticated) {
          console.log("[AUTH] ⚠️ Token removido! Limpando sessão...");
          clearLocalSession();
          navigate("/", { replace: true });
          return;
        }

        // Se tem token, verificar se ainda é válido
        if (token && isAuthenticated) {
          try {
            const response = await api.get("/me", {
              timeout: 10000,
              ...( { skipUnauthorizedHandler: true } as any),
            });
            if (!response.data?.user) {
              if (isDesktopClient()) {
                console.warn("[AUTH] /me sem usuário no desktop. Mantendo sessão e aguardando próxima validação.");
                return;
              }
              console.log("[AUTH] ⚠️ Token inválido! Limpando sessão...");
              clearLocalSession();
              navigate("/", { replace: true });
            } else {
              refreshTokenFromResponse(response.data);
            }
          } catch (error) {
            console.error("[AUTH] ⚠️ Erro ao verificar token:", error);
            if (isAuthFailure(error)) {
              if (isDesktopClient()) {
                const sessionRecovered = await revalidateSessionSafely();
                if (sessionRecovered) {
                  console.log("[AUTH] Sessão desktop revalidada após falha de autenticação.");
                  return;
                }
                console.warn("[AUTH] Falha de autenticação no desktop sem recuperação imediata. Sessão mantida.");
                return;
              }
              clearLocalSession();
              navigate("/", { replace: true });
            }
          }
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [isAuthenticated, revalidateSessionSafely, clearLocalSession, navigate]);

  // Keepalive periódico no desktop: renova o token silenciosamente a cada 20 min
  useEffect(() => {
    if (!isAuthenticated || !isDesktopClient()) return;

    const KEEPALIVE_INTERVAL_MS = 20 * 60 * 1000; // 20 minutos

    const keepalive = async () => {
      try {
        const response = await api.get("/me", {
          timeout: 10000,
          ...({ skipUnauthorizedHandler: true } as any),
        });
        if (response.data?.user) {
          refreshTokenFromResponse(response.data);
        }
      } catch (err) {
        console.warn("[AUTH] Keepalive falhou (não-fatal):", err);
      }
    };

    const id = setInterval(keepalive, KEEPALIVE_INTERVAL_MS);
    return () => clearInterval(id);
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated || !currentUser?.id || !isDesktopClient()) return;

    const onVisibleMirrorSync = () => {
      if (document.visibilityState !== "visible") return;
      void syncDesktopMirrorNow({
        userId: currentUser.id,
        trigger: "visibility",
      }).catch((error) => {
        console.warn("[MIRROR] Falha no sync por visibilidade:", error);
      });
    };

    const intervalId = window.setInterval(() => {
      void syncDesktopMirrorNow({
        userId: currentUser.id,
        trigger: "interval",
      }).catch((error) => {
        console.warn("[MIRROR] Falha no sync periódico:", error);
      });
    }, 5 * 60 * 1000);

    document.addEventListener("visibilitychange", onVisibleMirrorSync);
    return () => {
      clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisibleMirrorSync);
    };
  }, [isAuthenticated, currentUser?.id]);

  let lastActivityTime = 0;

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        onLogout,
        onSignIn,
        onSignInQRCode,
        user: authStore.getUser(),
        showKeyModal,
        setShowKeyModal, // Provedor para controlar a visibilidade do modal
        onExpireToken,
      }}
    >
      {children}
      {showKeyModal && <UserRegistrationPairKey />} {/* Renderiza o modal se necessário */}
    </AuthContext.Provider>
  );
};

const useAuth = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth must be used within a AuthProvider");
  }

  return context;
};

export { AuthProvider, useAuth };
